import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { procurementApi, type PendingApprovalResponse } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProcurementApprovalsPage() {
  const { hasAny } = usePermissions();
  const canAct = hasAny('PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_PO_APPROVE', 'PROCUREMENT_MANAGE');
  const [items, setItems] = useState<PendingApprovalResponse[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const { rows: paginatedItems, tablePaginationProps: itemsPaginationProps } = useClientPaginatedRows(items);

  const load = async () => {
    try {
      setItems(await procurementApi.listPendingApprovals());
    } catch (err) {
      showApiError(err, 'Failed to load pending approvals');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const act = async (item: PendingApprovalResponse, action: 'APPROVE' | 'REJECT') => {
    setBusyId(item.instanceId);
    try {
      if (item.documentType === 'PURCHASE_REQUEST') {
        await procurementApi.approvePurchaseRequest(item.documentId, action);
      } else if (item.documentType === 'PURCHASE_ORDER') {
        await procurementApi.approvePurchaseOrder(item.documentId, action);
      } else {
        notify.error(`Approval not supported for ${item.documentType}`);
        return;
      }
      notify.success(action === 'APPROVE' ? 'Approved' : 'Rejected');
      await load();
    } catch (err) {
      showApiError(err, `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  const docLink = (item: PendingApprovalResponse) => {
    if (item.documentType === 'PURCHASE_REQUEST') {
      return '/procurement/purchase-requests';
    }
    if (item.documentType === 'PURCHASE_ORDER') {
      return '/procurement/purchase-orders';
    }
    return '/procurement';
  };

  return (
    <div>
      <PageHeader
        title="Pending approvals"
        description="Documents awaiting your approval action"
        breadcrumbs={[
          { label: 'Procurement', to: '/procurement' },
          { label: 'Approvals' },
        ]}
      />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Document</th>
              <th className="p-3">Type</th>
              <th className="p-3">Stage</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-slate-500">
                  No documents pending your approval.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
              <tr key={item.instanceId} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3">
                  <Link to={docLink(item)} className="font-medium text-primary-600 hover:underline">
                    {item.documentNumber ?? `#${item.documentId}`}
                  </Link>
                </td>
                <td className="p-3">{item.documentType.replace('_', ' ')}</td>
                <td className="p-3">{item.currentStageOrder}</td>
                <td className="p-3">
                  {item.totalAmount != null ? formatCurrency(item.totalAmount) : '—'}
                </td>
                <td className="p-3">
                  {canAct ? (
                    <div className="flex gap-2">
                      <Button
                        className="!px-2 !py-1 text-xs"
                        loading={busyId === item.instanceId}
                        onClick={() => void act(item, 'APPROVE')}
                      >
                        Approve
                      </Button>
                      <Button
                        className="!px-2 !py-1 text-xs"
                        variant="ghost"
                        loading={busyId === item.instanceId}
                        onClick={() => void act(item, 'REJECT')}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">Approval permission required</span>
                  )}
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...itemsPaginationProps} hasRows={items.length > 0} />
      </div>
    </div>
  );
}
